/**
 * Hook: filtragem e busca do Kanban (role, status, texto, tags, data, agente, tarefas).
 */

import { useState, useMemo, useEffect, useRef } from 'react';
import { Lead, FunnelStage, Task } from '../../../types';
import { Filters } from '../../../components/shared/filters/types';
import { usePermissions } from '../../../hooks/usePermissions';
import { useAuth } from '../../../contexts/AuthContext';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { KANBAN_OTHER_FUNNEL_STAGE_ID } from '../constants';
import {
    SEARCH_KEY,
    FILTERS_KEY,
    GLOBAL_STAGES,
    hasWindow,
    sanitizeIlike,
    toLead,
    readStoredSearch,
    readStoredFilters,
    sanitizeFiltersForViewer,
    matchesLead,
    collectContactIds,
    fetchOppsInStages,
} from '../utils/kanbanFilterHelpers';

export const useKanbanFilters = (leads: Lead[], funnelStages: FunnelStage[], tasks: Task[] = []) => {
    const { canViewAllLeads, canFilterByAgent } = usePermissions();
    const userId = useAuth().user?.id;
    const { currentOrganization } = useOrganization();
    const orgId = currentOrganization?.id;
    const orgRole = currentOrganization?.role;
    const genRef = useRef(0);

    const [searchTerm, setSearchTerm] = useState(readStoredSearch);
    const [filters, setFilters] = useState<Filters>(readStoredFilters);
    const [orgSearchLeads, setOrgSearchLeads] = useState<Lead[]>([]);

    // Papel conhecido + sem UI de responsável → remove agents/noAgent stale do storage.
    useEffect(() => {
        if (!orgRole || canFilterByAgent) return;
        setFilters((prev) => sanitizeFiltersForViewer(prev, false));
    }, [orgRole, canFilterByAgent]);

    const effectiveFilters = useMemo(
        () => (!orgRole ? filters : sanitizeFiltersForViewer(filters, canFilterByAgent)),
        [filters, canFilterByAgent, orgRole],
    );

    useEffect(() => {
        if (hasWindow()) localStorage.setItem(FILTERS_KEY, JSON.stringify(filters));
    }, [filters]);

    useEffect(() => {
        if (!hasWindow()) return;
        try { sessionStorage.setItem(SEARCH_KEY, searchTerm); } catch { /* ignore */ }
    }, [searchTerm]);

    // Etapa filtrada deve pertencer ao funil atual (senão o board fica vazio após trocar funil)
    useEffect(() => {
        const stage = effectiveFilters.stage;
        if (GLOBAL_STAGES.has(stage)) return;
        if (!funnelStages.some((s) => s.id === stage)) {
            setFilters((prev) => (prev.stage === stage ? { ...prev, stage: 'all' } : prev));
        }
    }, [funnelStages, effectiveFilters.stage]);

    const leadsPool = useMemo(() => {
        const map = new Map(leads.map((l) => [l.id, l] as const));
        for (const l of orgSearchLeads) if (!map.has(l.id)) map.set(l.id, l);
        return Array.from(map.values());
    }, [leads, orgSearchLeads]);

    useEffect(() => {
        const q = searchTerm.trim();
        const stageIds = funnelStages.map((s) => s.id);
        if (q.length < 2 || !orgId || stageIds.length === 0) { setOrgSearchLeads([]); return; }
        const safe = sanitizeIlike(q);
        if (!safe) { setOrgSearchLeads([]); return; }

        const pattern = `%${safe}%`;
        const digits = q.replace(/\D/g, '');
        const gen = ++genRef.current;

        const t = window.setTimeout(() => {
            void (async () => {
                try {
                    const [{ data: titleRows, error: titleErr }, contactIds] = await Promise.all([
                        fetchOppsInStages(orgId, stageIds, { titlePattern: pattern }),
                        collectContactIds(orgId, [
                            { column: 'name', pattern, limit: 60 },
                            { column: 'email', pattern, limit: 60 },
                        ]),
                    ]);
                    if (gen !== genRef.current) return;
                    if (titleErr) {
                        console.error('[useKanbanFilters] org search title', titleErr);
                        setOrgSearchLeads([]);
                        return;
                    }

                    if (digits.length >= 5) {
                        const digitPat = `%${digits}%`;
                        const phoneIds = await collectContactIds(orgId, [
                            { column: 'phone', pattern: digitPat, limit: 40 },
                            { column: 'whatsapp', pattern: digitPat, limit: 40 },
                        ]);
                        if (gen !== genRef.current) return;
                        for (const id of phoneIds) contactIds.add(id);
                    }

                    let contactRows: unknown[] = [];
                    if (contactIds.size > 0) {
                        const { data: byContact, error: cErr } = await fetchOppsInStages(
                            orgId, stageIds, { contactIds: [...contactIds] },
                        );
                        if (gen !== genRef.current) return;
                        if (cErr) console.error('[useKanbanFilters] org search by contact', cErr);
                        else contactRows = byContact || [];
                    }

                    const merged = new Map<string, unknown>();
                    for (const row of [...(titleRows || []), ...contactRows]) {
                        merged.set((row as { id: string }).id, row);
                    }
                    if (gen !== genRef.current) return;
                    setOrgSearchLeads([...merged.values()].map(toLead));
                } catch (e) {
                    console.error('[useKanbanFilters] org search', e);
                    if (gen === genRef.current) setOrgSearchLeads([]);
                }
            })();
        }, 320);

        return () => window.clearTimeout(t);
    }, [searchTerm, orgId, funnelStages]);

    const visibleLeads = useMemo(
        () => (canViewAllLeads ? leadsPool : leadsPool.filter((l) => l.agent === userId)),
        [leadsPool, canViewAllLeads, userId],
    );

    const filteredLeads = useMemo(
        () => visibleLeads.filter((lead) => matchesLead(lead, effectiveFilters, searchTerm, tasks)),
        [visibleLeads, searchTerm, effectiveFilters, tasks],
    );

    const leadsByStage = useMemo(() => {
        const result: Record<string, Lead[]> = { [KANBAN_OTHER_FUNNEL_STAGE_ID]: [] };
        for (const s of funnelStages) result[s.id] = [];
        for (const lead of filteredLeads) {
            if (!lead.stage) continue;
            (result[lead.stage] ?? result[KANBAN_OTHER_FUNNEL_STAGE_ID]).push(lead);
        }
        return result;
    }, [funnelStages, filteredLeads]);

    return { searchTerm, setSearchTerm, filters: effectiveFilters, setFilters, filteredLeads, leadsByStage };
};
